"use client";

import { useCustomEditor } from "@/providers/custom-editor-provider";
import {
	AtSignIcon,
	BoldIcon,
	ImageIcon,
	ItalicIcon,
	LinkIcon,
	ListIcon,
	ListOrderedIcon,
	MinusIcon,
	QuoteIcon,
	Redo2Icon,
	StrikethroughIcon,
	TableIcon,
	UnderlineIcon,
	Undo2Icon,
	YoutubeIcon,
} from "lucide-react";
import { useCallback } from "react";
import { EmojiButton } from "../../../extensions/emoji/emoji-option-button";
import { AlignmentFormatPopover } from "./alignment-format-popover";
import { BlockFormatPopover } from "./block-format-popover";
import { EditorOptionButton } from "./editor-option-button";
import TextFormatPopover from "./text-format-popver";

const ArticleEditorOptions = ({ allowedFormats }: { allowedFormats: string[] }) => {
	const { editor, openMediaUploader } = useCustomEditor();

	const isEditorDisabled = !editor?.isEditable;

	const setLink = useCallback(() => {
		if (!editor) return;

		const previousUrl = editor.getAttributes("link").href;
		const url = window.prompt("URL", previousUrl);

		if (url === null) return;

		if (url === "") {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			return;
		}

		editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
	}, [editor]);

	const addYouTubeVideo = useCallback(() => {
		const url = prompt("Enter YouTube URL");

		if (editor && url) {
			editor.commands.setYoutubeVideo({ src: url });
		}
	}, [editor]);

	if (!editor) return null;

	return (
		<div className="flex items-center gap-px max-md:flex-wrap">
			<div className="flex items-center gap-px max-md:flex-wrap">
				<EditorOptionButton
					title="Bold"
					icon={BoldIcon}
					active={editor.isActive("bold")}
					onClick={() => editor.chain().focus().toggleBold().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleBold().run()}
					hidden={!allowedFormats.includes("bold")}
				/>

				<EditorOptionButton
					title="Italic"
					icon={ItalicIcon}
					active={editor.isActive("italic")}
					onClick={() => editor.chain().focus().toggleItalic().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleItalic().run()}
					hidden={!allowedFormats.includes("italic")}
				/>

				<EditorOptionButton
					title="Underline"
					icon={UnderlineIcon}
					active={editor.isActive("underline")}
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleUnderline().run()}
					hidden={!allowedFormats.includes("underline")}
				/>

				<EditorOptionButton
					title="Link"
					icon={LinkIcon}
					onClick={setLink}
					active={editor.isActive("link")}
					hidden={!allowedFormats.includes("link")}
					disabled={isEditorDisabled}
				/>

				<EmojiButton
					onEmojiSelect={(unicode) => editor.chain().focus().insertContent(unicode).run()}
				/>

				<button
					type="button"
					onClick={() => editor.chain().focus().insertContent("@").run()}
					className="flex items-center justify-center gap-1 rounded-r-md px-2 py-1 text-sm hover:bg-brand-primary hover:text-white"
				>
					<AtSignIcon className="h-4 w-4" />
				</button>

				<EditorOptionButton
					title="Strikethrough"
					icon={StrikethroughIcon}
					active={editor.isActive("strike")}
					onClick={() => editor.chain().focus().toggleStrike().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleStrike().run()}
					hidden={!allowedFormats.includes("strike")}
				/>

				<EditorOptionButton
					title="Table"
					icon={TableIcon}
					active={editor.isActive("table")}
					// TODO: Add ability for the user to enter rows and columns count
					onClick={() => editor && editor.chain().focus().insertTable({}).run()}
					disabled={
						isEditorDisabled || (editor && !editor.can().chain().focus().insertTable().run())
					}
					hidden={!allowedFormats.includes("table")}
				/>

				<EditorOptionButton
					title="Numbered List"
					icon={ListOrderedIcon}
					active={editor.isActive("orderedList")}
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleOrderedList().run()}
					hidden={!allowedFormats.includes("orderedList")}
				/>

				<EditorOptionButton
					title="Bullet List"
					icon={ListIcon}
					active={editor.isActive("bulletList")}
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleBulletList().run()}
					hidden={!allowedFormats.includes("bulletList")}
				/>

				<EditorOptionButton
					title="Blockquote"
					icon={QuoteIcon}
					active={editor.isActive("blockquote")}
					onClick={() => editor.chain().focus().toggleBlockquote().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleBlockquote().run()}
					hidden={!allowedFormats.includes("blockquote")}
				/>

				<EditorOptionButton
					title="Image"
					icon={ImageIcon}
					active={editor.isActive("image")}
					onClick={() => openMediaUploader(true)}
					hidden={!allowedFormats.includes("image")}
					disabled={isEditorDisabled}
				/>

				<EditorOptionButton
					title="YouTube"
					icon={YoutubeIcon}
					active={editor.isActive("youtube")}
					onClick={addYouTubeVideo}
					hidden={!allowedFormats.includes("youtube")}
					disabled={isEditorDisabled}
				/>

				<EditorOptionButton
					title="Divider"
					icon={MinusIcon}
					active={editor.isActive("horizontalRule")}
					onClick={() => editor.chain().focus().setHorizontalRule().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().setHorizontalRule().run()}
					hidden={!allowedFormats.includes("horizontalRule")}
				/>

				<TextFormatPopover editor={editor} />

				<BlockFormatPopover editor={editor} />

				<AlignmentFormatPopover editor={editor} />

				<EditorOptionButton
					title="Undo"
					icon={Undo2Icon}
					onClick={() => editor.chain().focus().undo().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().undo().run()}
					hidden={!allowedFormats.includes("undo")}
				/>

				<EditorOptionButton
					title="Redo"
					icon={Redo2Icon}
					onClick={() => editor.chain().focus().redo().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().redo().run()}
					hidden={!allowedFormats.includes("redo")}
				/>
			</div>
		</div>
	);
};

export default ArticleEditorOptions;
